import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ArrowDown, HandCoins } from "lucide-react";
import { nextPrayer, countdown } from "@/lib/format";

const HERO_IMG =
  "https://images.unsplash.com/photo-1528862973381-9bc5ad6d4227?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export const Hero = ({ profile, prayer }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const next = nextPrayer(prayer?.times, now);

  return (
    <section id="top" className="relative emerald-bg pattern-overlay text-emerald-50 overflow-hidden">
      <img src={HERO_IMG} alt="Interior masjid" className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-luminosity" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0d3b2e] via-[#0d3b2e]/90 to-[#0d3b2e]/40" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 space-y-7">
          <p className="font-arabic text-3xl sm:text-4xl gold-text rise">السَّلاَمُ عَلَيْكُمْ وَرَحْمَةُ اللهِ وَبَرَكَاتُهُ</p>
          <div className="rise delay-1">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#80b996]">Selamat datang di</p>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white" data-testid="hero-title">
              Al-Iman Digital
            </h1>
          </div>
          <p className="text-base sm:text-lg text-emerald-100/85 max-w-xl leading-relaxed rise delay-2" data-testid="hero-description">
            Sistem Informasi dan Manajemen Masjid Al-Iman hadir untuk menghubungkan pengelolaan masjid dengan informasi dan layanan bagi jamaah serta masyarakat.
          </p>
          <div className="flex flex-wrap gap-3 rise delay-3">
            <Link to="/kegiatan" className="inline-flex items-center gap-2 rounded-full bg-[#d4af37] text-emerald-950 font-semibold px-6 py-3 hover:bg-[#e2c15a] transition-colors" data-testid="hero-cta-kegiatan">
              <CalendarDays size={16} /> Jadwal Kegiatan
            </Link>
            <Link to="/donasi" className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white font-semibold px-6 py-3 hover:bg-white/10 transition-colors" data-testid="hero-cta-infaq">
              <HandCoins size={16} /> Donasi Online
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 rise delay-4">
          <div className="rounded-3xl glass-dark border border-white/15 p-7 shadow-2xl relative overflow-hidden" data-testid="hero-prayer-countdown">
            <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#d4af37]/20 blur-2xl" />
            <p className="text-xs uppercase tracking-widest text-[#80b996] font-semibold">Waktu sholat berikutnya</p>
            {next ? (
              <>
                <div className="mt-4 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-4xl sm:text-5xl font-extrabold text-white" data-testid="hero-next-prayer-name">{next.label}</p>
                    <p className="text-emerald-200 text-sm mt-1">{next.tomorrow ? "Besok" : "Hari ini"} · {prayer?.lokasi}</p>
                  </div>
                  <p className="text-4xl font-bold gold-text" data-testid="hero-next-prayer-time">{next.time}</p>
                </div>
                <div className="mt-6 rounded-2xl bg-black/20 px-5 py-4 flex items-center justify-between">
                  <span className="text-sm text-emerald-200">Menuju {next.label}</span>
                  <span className="font-mono text-2xl font-bold tracking-wider text-white" data-testid="hero-countdown-timer">{countdown(next.remainingMin)}</span>
                </div>
                <div className="mt-5 grid grid-cols-5 gap-2 text-center">
                  {["subuh", "dzuhur", "ashar", "maghrib", "isya"].map((k) => (
                    <div key={k} className={`rounded-xl py-2 ${next.key === k ? "bg-[#d4af37] text-emerald-950" : "bg-white/5 text-emerald-100"}`}>
                      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-80">{k}</p>
                      <p className="text-sm font-bold">{prayer?.times?.[k]}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="mt-4 text-emerald-200">Memuat jadwal...</p>
            )}
            <p className="mt-4 text-[11px] text-emerald-300/70">{prayer?.hari}, {prayer?.tanggal} · {prayer?.source === "auto" ? "Sumber: Kemenag RI (Kab. Ciamis)" : "Jadwal ditetapkan DKM"}</p>
            <Link to="/jadwal-sholat" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#d4af37] hover:underline" data-testid="hero-link-jadwal-lengkap">
              Lihat jadwal lengkap bulan ini →
            </Link>
          </div>
        </div>
      </div>
      <a href="#kegiatan" className="absolute bottom-5 left-1/2 -translate-x-1/2 text-emerald-300/60 animate-bounce" aria-label="Scroll">
        <ArrowDown size={20} />
      </a>
    </section>
  );
};
